import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useBillNumberFormSchema } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/components/BillNumberForm/useBillNumberFormSchema";
import { Button } from "@components/Button";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Notification } from "@components/Notification/Notification";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useJointBillErrorsStore } from "@store/jointBillErrors/jointBillErrors";
import { useUpdateBillNumberMutation } from "@services/bills/mutations";
import {
  type IBillData,
  type IOverviewJointBillService,
} from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";

interface IFormData {
  billNumber: string;
}

interface IBillNumberFormProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
  billId: IBillData["billId"];
}

export const BillNumberForm: React.FC<IBillNumberFormProps> = ({
  jointBillServiceId,
  periodId,
  billId,
}) => {
  const { t } = useTranslation();

  const schema = useBillNumberFormSchema();
  const { handleSubmit, control, setError } = useForm<IFormData>({
    resolver: zodResolver(schema),
  });
  const { mutateAsync: updateBillNumber, isPending } =
    useUpdateBillNumberMutation({
      jointBillServiceId,
      periodId,
      billId,
    });
  const { billNumberErrors, clearBillNumberErrors } = useJointBillErrorsStore();
  const { errors: formErrors } = useFormState({ control });
  const isFormError = !!formErrors.billNumber;

  const { setApiErrors } = useApiErrorUtils(setError);

  const submit = async (data: IFormData) => {
    clearBillNumberErrors();
    const response = await updateBillNumber(data.billNumber).catch(
      setApiErrors,
    );

    if (!isNullOrUndefined(response)) {
      toast.success(t("billNumberForm.toast.success"));
    }
  };

  useEffect(() => {
    const error = billNumberErrors.find(
      (field) => field.billId === billId && field.fieldName === "billNumber",
    );

    if (error) {
      setError("billNumber", {
        message: error.text,
      });
    }
  }, [billNumberErrors, billId, setError]);

  return (
    <Notification
      description={t("billNumberForm.description")}
      button={
        <form
          className="flex items-center gap-2"
          onSubmit={handleSubmit(submit)}
          id={isFormError ? "InvalidBillNumberForm" : undefined}
        >
          <FormInput className="w-44" name="billNumber" control={control} />
          <Button className="flex-shrink-0" isLoading={isPending}>
            {t("billNumberForm.actions.save")}
          </Button>
        </form>
      }
    />
  );
};
